import React, { useMemo, useState } from 'react';
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import Button from '../../../components/ui/Button';

const CalendarGrid = ({
  currentDate,
  invoicesByDate,
  onDateSelect,
  selectedDate,
  dateBasis,
  onViewWeekReport
}) => {
  const [hoveredDate, setHoveredDate] = useState(null);

  const days = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [currentDate]);

  const weeks = useMemo(() => {
    const rows = [];
    for (let i = 0; i < days.length; i += 7) {
      rows.push(days.slice(i, i + 7));
    }
    return rows;
  }, [days]);

  const getDateKey = (date) => format(date, 'yyyy-MM-dd');

  const formatCurrency = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return '£0.00';
    return `£${n.toFixed(2)}`;
  };

  const getDayStatus = (dayInvoices) => {
    if (!dayInvoices || dayInvoices.length === 0) return 'none';

    if (dateBasis === 'created_at') {
      const hasNeedsReview = dayInvoices.some((inv) => inv?.status === 'needs_review');
      if (hasNeedsReview) return 'red';
      return 'green';
    }

    const hasMismatch = dayInvoices.some((inv) => inv?.trust && !inv?.trust?.reconciled);
    const hasUntrusted = dayInvoices.some((inv) => inv?.trust && !inv?.trust?.trusted);
    if (hasMismatch || hasUntrusted) return 'red';
    return 'green';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'red':
        return 'bg-error';
      case 'green':
        return 'bg-success';
      default:
        return 'bg-muted-foreground/40';
    }
  };

  const renderCalendarDay = (date) => {
    const dateKey = getDateKey(date);
    const dayInvoices = invoicesByDate?.[dateKey] || [];
    const isCurrentMonth = isSameMonth(date, currentDate);
    const isSelected = selectedDate && isSameDay(date, selectedDate);
    const isToday = isSameDay(date, new Date());
    const isHovered = hoveredDate && isSameDay(date, hoveredDate);

    const status = getDayStatus(dayInvoices);
    const totalGross = dayInvoices.reduce((sum, inv) => sum + (inv?.grossAmount || 0), 0);
    const countLabel = `${dayInvoices.length} Invoice${dayInvoices.length === 1 ? '' : 's'}`;
    const needsReviewCount = dayInvoices.filter(
      (inv) => inv?.status === 'needs_review' || inv?.status === 'parsed'
    ).length;
    const reviewedCount = dayInvoices.filter(
      (inv) => inv?.status === 'reviewed' || inv?.status === 'exported'
    ).length;

    return (
      <button
        key={dateKey}
        onClick={() => onDateSelect?.(date, dayInvoices)}
        onMouseEnter={() => setHoveredDate(date)}
        onMouseLeave={() => setHoveredDate(null)}
        className={`
          relative w-full h-20 p-2 border rounded-lg transition-all duration-200
          ${isCurrentMonth ? 'text-foreground' : 'text-muted-foreground opacity-40'}
          ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}
          ${isToday ? 'border-primary font-semibold' : 'border-border'}
          hover:shadow-sm hover:scale-[1.02]
          focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
        `}
      >
        <div className="flex items-center justify-between mb-1">
          <span className={`text-sm ${isToday ? 'font-bold' : 'font-medium'}`}>
            {date.getDate()}
          </span>
          {dayInvoices.length > 0 && (
            <span className={`w-2 h-2 rounded-full ${getStatusColor(status)}`} />
          )}
        </div>

        {dayInvoices.length > 0 && (
          <div className="space-y-1">
            {dateBasis === 'created_at' ? (
              <div className="space-y-1">
                <div className="text-sm font-medium text-foreground">{countLabel}</div>
                <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-medium">
                  <span className="inline-flex items-center rounded-full bg-error/10 text-error px-2 py-0.5">
                    {needsReviewCount} NR
                  </span>
                  <span className="inline-flex items-center rounded-full bg-success/10 text-success px-2 py-0.5">
                    {reviewedCount} Rev
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-sm font-mono text-foreground">{formatCurrency(totalGross)}</div>
            )}
          </div>
        )}

        {isHovered && dayInvoices.length > 0 && (
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-10 bg-popover border border-border rounded-md shadow-moderate p-3 min-w-48">
            <div className="text-sm font-medium text-popover-foreground mb-2">
              {format(date, 'EEEE, d MMMM')}
            </div>
            <div className="space-y-1 text-xs text-muted-foreground">
              <div className="flex justify-between">
                <span>{dateBasis === 'created_at' ? 'Uploads:' : 'Invoices:'}</span>
                <span className="font-medium text-foreground">{dayInvoices.length}</span>
              </div>
              {dateBasis === 'invoice_date' && (
                <div className="flex justify-between">
                  <span>Gross total:</span>
                  <span className="font-medium text-foreground">{formatCurrency(totalGross)}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </button>
    );
  };

  const legendItems = useMemo(() => {
    if (dateBasis === 'created_at') {
      return [
        { label: 'Needs review', color: 'bg-error' },
        { label: 'All reviewed / exported', color: 'bg-success' }
      ];
    }
    return [
      { label: 'Untrusted or mismatch', color: 'bg-error' },
      { label: 'Trusted', color: 'bg-success' }
    ];
  }, [dateBasis]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
        {legendItems.map((item) => (
          <div key={item.label} className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${item.color}`} />
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-[64px_repeat(7,minmax(0,1fr))] bg-muted border-b border-border">
          <div className="p-3 text-center text-xs font-medium text-muted-foreground">Week</div>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="p-3 text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>
        <div className="bg-border">
          {weeks.map((weekDays) => {
            const weekStart = startOfWeek(weekDays[0], { weekStartsOn: 0 });
            const weekEnd = endOfWeek(weekDays[0], { weekStartsOn: 0 });
            const rangeLabel = `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d')}`;
            return (
              <div key={format(weekStart, 'yyyy-MM-dd')} className="grid grid-cols-[64px_repeat(7,minmax(0,1fr))] gap-px bg-border">
                <div className="bg-card flex items-center justify-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="FileText"
                    onClick={() => onViewWeekReport?.(weekStart, weekEnd)}
                    title={`Weekly report (${rangeLabel})`}
                    className="w-9 h-9 p-0"
                  />
                </div>
                {weekDays.map((date) => renderCalendarDay(date))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CalendarGrid;
